import React from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Box,
    Typography,
    Chip,
    LinearProgress,
    Grid2,
    Tooltip
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { PositionHealth } from '../../types/DashboardTypes';

interface PositionHealthCardProps {
    position: PositionHealth;
    contractAddress?: string;
}

const PositionHealthCard: React.FC<PositionHealthCardProps> = ({
    position,
    contractAddress
}) => {
    const formatLiquidity = (liquidity: string) => {
        const value = parseFloat(liquidity);
        if (value >= 1e18) {
            return (value / 1e18).toFixed(2) + 'E18';
        }
        return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
    };

    const formatFees = (fees: string) => {
        const value = parseFloat(fees);
        if (value >= 1e18) {
            return (value / 1e18).toFixed(6);
        }
        return (value / 1e18).toFixed(8);
    };

    const getTickPosition = () => {
        const [lower, upper] = position.tickRange;
        const current = position.currentTick;
        const range = upper - lower;
        const position_in_range = ((current - lower) / range) * 100;
        return Math.max(0, Math.min(100, position_in_range));
    };

    const getHealthColor = () => {
        if (position.inRange) {
            return 'success';
        }
        return 'warning';
    };

    const getHealthIcon = () => {
        if (position.inRange) {
            return <CheckCircleIcon fontSize="small" />;
        }
        return <WarningIcon fontSize="small" />;
    };

    const getTickRangeDescription = () => {
        const [lower, upper] = position.tickRange;
        if (lower === -887220 && upper === 887220) {
            return 'Full Range Position';
        }
        return `Custom Range (${lower} to ${upper})`;
    };

    return (
        <Card elevation={2}>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" fontWeight="bold">
                            Position #{position.tokenId}
                        </Typography>
                        <Chip
                            icon={getHealthIcon()}
                            label={position.inRange ? "In Range" : "Out of Range"}
                            color={getHealthColor()}
                            size="small"
                        />
                    </Box>
                }
                subheader={`Last updated: ${new Date(position.lastUpdated).toLocaleTimeString()}`}
            />
            <CardContent>
                <Grid2 container spacing={2}>
                    {/* Position Status */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                Position Status
                            </Typography>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="body2" color="textSecondary">
                                    Current Tick:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {position.currentTick.toLocaleString()}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="body2" color="textSecondary">
                                    Range:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {getTickRangeDescription()}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="textSecondary">
                                    Liquidity:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatLiquidity(position.liquidity)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid2>

                    {/* Tick Range Visualization */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ mb: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Tick Position
                                </Typography>
                                <Tooltip title="Shows where the current price is within your position range">
                                    <InfoIcon fontSize="small" color="action" />
                                </Tooltip>
                            </Box>

                            <Box sx={{ position: 'relative', mb: 1 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={100}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: position.inRange ? 'success.main' : 'grey.300'
                                        }
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -2,
                                        left: `${getTickPosition()}%`,
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: position.inRange ? 'primary.main' : 'warning.main',
                                        border: '2px solid white',
                                        transform: 'translateX(-50%)',
                                        zIndex: 1
                                    }}
                                />
                            </Box>

                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="caption" color="textSecondary">
                                    {position.tickRange[0].toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Current: {position.currentTick.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {position.tickRange[1].toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid2>

                    {/* Unclaimed Fees */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.08)', borderRadius: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                💰 Unclaimed Fees
                            </Typography>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2" color="textSecondary">
                                    WETH Fees:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatFees(position.unclaimedFees0)} WETH
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2" color="textSecondary">
                                    MEMER Fees:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatFees(position.unclaimedFees1)} MEMER
                                </Typography>
                            </Box>
                        </Box>
                    </Grid2>

                    {/* Health Insights */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ mt: 1 }}>
                            {position.inRange ? (
                                <Typography variant="caption" color="success.main">
                                    ✅ Position is actively earning fees from trades
                                </Typography>
                            ) : (
                                <Typography variant="caption" color="warning.main">
                                    ⚠️ Position is out of range and not earning fees
                                </Typography>
                            )}
                        </Box>
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    );
};

export default PositionHealthCard;
